/**
 * CAF — Coverage AutoFill tier attribution (DAM / Provider / AI Research).
 *
 * Live-prod symptom (2026-05-18, customer trust): when no documents are
 * uploaded the Coverage Matrix reads `5/5 missing`, even though the
 * DCO orchestrator has already filled several slots via the curated DAM
 * (Tier 3) and per-slot grounded AI Research (Tier 4). The chip strip
 * (EVL-3) surfaces provider / doc / authored counts, but it never tells
 * the user "this row went Strong VIA DAM" or "this row went Adequate
 * VIA Research". The customer reads the legacy `5/5 missing` copy and
 * concludes the platform did nothing — even though the SED actually
 * carries the filled values.
 *
 * Hallucination guard (HARD RULE per the CAF brief):
 *   Every attribution this module emits is sourced from a SED slot's
 *   `status` (`'verified-from-dam'` / `'verified-from-research'`) and
 *   the slot's `provenanceDetail` chain. We never invent an attribution
 *   from a chip count alone — if no `sedSlots` row carries the tier
 *   status, the badge does NOT render.
 *
 * Composition:
 *   - CAF-1 — pure `attributeRowTier(lineage)` returns the per-row tier
 *     attribution ('dam' / 'research' / 'provider' / 'doc' / 'authored'
 *     / 'prior-report' / 'none'). Fed by `lineage.sources.sedSlots[i].status`
 *     and `lineage.sources.authored[]` / `priorReports[]`.
 *   - CAF-2 — pure `aggregateTierAttribution(rows)` totals the
 *     attributions across all rows so the footer reads "2/5 strong
 *     (via DAM) + 1/5 strong (via Research)" style copy.
 *   - CAF-3 — `<CoverageTierBadge>` sub-component renders the per-row
 *     attribution as a small inline chip next to the Best Status cell.
 *     `<CoverageTierFooter>` renders the aggregated summary line.
 *
 * Reuses shipped surfaces (no fork):
 *   - `RequirementLineage` (EVL-1) — single source of truth for the row.
 *   - `verified-from-dam` / `verified-from-research` — DCO Wave-A widened
 *     statuses on `EvidenceStatus`.
 *   - `applyDamToSlot` / `applyResearchToSlot` — DCO writers that stamp
 *     the statuses we read here.
 *
 * Co-existence with CAR (concurrent): this module is additive only.
 * It does NOT mutate `RequirementLineage`, does NOT add fields to the
 * SED, does NOT touch the orchestrator. If CAR deepens
 * `researchDispatcher`, the new fields surface here automatically via
 * the `sedSlots[*].status` read.
 */
import React from 'react';
import type {
  RequirementLineage,
  SedSlotLineage,
} from '../../../lib/evidenceLineage/requirementLineage';
import type { RequirementStatus } from '../../../lib/coverage/requirementStatus';

/**
 * Per-row tier attribution. Mirrors DCO's tier ladder verbatim:
 *
 *   Tier 1 — authored          (`'authored'`)
 *   Tier 2 — uploaded doc      (`'doc'`)
 *   Tier 2b — prior report     (`'prior-report'`)
 *   Tier 3 — DAM curated       (`'dam'`)
 *   Tier 4 — AI Research       (`'research'`)
 *   Tier 5 — provider          (`'provider'`)
 *   Tier 6 — none              (`'none'`)
 *
 * `'none'` is emitted when the row's status is `'missing'` OR when the
 * row's status is `'tentative'` (DCT score only — no slot was actually
 * filled). This is intentional: a tentative row is NOT an autofill,
 * it's a doc-match heuristic, and the tier badge would be misleading.
 */
export type CoverageRowTier =
  | 'authored'
  | 'doc'
  | 'prior-report'
  | 'dam'
  | 'research'
  | 'provider'
  // LX-3 LLW-FOLLOWUP-LITERATURE-IN-COVERAGE — Coverage Matrix surfaces
  // the live-literature (LLW) channel as the WEAKEST positive tier so
  // rows that otherwise would read 'none' carry a "Live lit available"
  // hint when the LLW orchestrator returned entries. NOT a per-row
  // fill — the LLW channel is coarse over (site, reportType); the badge
  // is informational and reads cyan rather than the warmer DAM/Research
  // palette so the user can distinguish a literature reference from a
  // structured slot fill.
  | 'liveLit'
  | 'none';

/**
 * Priority order when a row's lineage carries multiple tier signals.
 * Higher = more authoritative. Matches DCO's "authored > doc > prior
 * report > DAM > research > provider" ladder so the badge tells the
 * user the STRONGEST source carrying the row.
 *
 * Rationale: a row that's `'verified-from-doc'` on `grid` AND
 * `'verified-from-dam'` on `electricity` should read "doc" — the
 * doc is the more authoritative tier. The badge collapses the row's
 * multi-slot lineage into a single source label per the brief's
 * "render DAM-tier fills with the existing chip" requirement.
 */
const TIER_RANK: Record<CoverageRowTier, number> = {
  authored: 7,
  doc: 6,
  'prior-report': 5,
  dam: 4,
  research: 3,
  provider: 2,
  // LX-3 — `liveLit` ranks BELOW provider (weakest positive tier) so
  // the per-row `attributeRowTier` only emits it when no stronger
  // source carries the row. Above `none` so the badge still renders
  // for rows that would otherwise read missing/tentative.
  liveLit: 1,
  none: 0,
};

/**
 * Map a single SED slot's status (DCO-stamped) to its tier label.
 * Returns `null` when the slot carries no tier signal (e.g. an
 * `'unavailable'` slot the orchestrator didn't fill).
 */
function slotTier(slot: SedSlotLineage): CoverageRowTier | null {
  const s = slot.status;
  if (s === 'verified-from-dam') return 'dam';
  if (s === 'verified-from-research') return 'research';
  if (s === 'verified-from-doc') return 'doc';
  if (s === 'verified-from-context') return 'authored';
  if (s === 'verified-from-report') return 'prior-report';
  if (s === 'verified' || s === 'anomalous') return 'provider';
  // KSD2 KMZ-derived slots land as `'verified-from-kmz'` which the
  // lineage projection tags `provenance === 'verified-from-kmz'`. KMZ
  // is a user-uploaded source — read it as `'doc'` for attribution.
  if ((s as string) === 'verified-from-kmz') return 'doc';
  return null;
}

/**
 * Pick the row's authoritative tier across all its populated slots,
 * authored claims, and prior-report bridge rows. Pure.
 *
 * Decision tree:
 *   1. Status `'missing'` / `'tentative'` → `'none'` (no autofill happened).
 *   2. Status `'authored*'` → `'authored'`.
 *   3. Otherwise walk `lineage.sources.sedSlots[*].status`, map each
 *      to a tier, and pick the highest-ranked.
 *   4. Fall-throughs: prior-report rows count as `'prior-report'`;
 *      authored claims count as `'authored'`. We re-check these even
 *      when the chip status is `'strong'` / `'adequate'` because
 *      the chip is doc/provider-driven and may miss the authored
 *      contribution.
 */
export function attributeRowTier(lineage: RequirementLineage): CoverageRowTier {
  const status: RequirementStatus = lineage.status;
  // Missing / tentative rows never carry a fill-tier badge. Live
  // literature is prompt-only (LLW bundle is site-wide, not per-row)
  // and is surfaced in the Sources section instead — pairing "Missing"
  // with a "Live lit" chip read as contradictory.
  if (status === 'missing' || status === 'tentative') return 'none';
  if (status === 'authored' || status === 'authored-strong') return 'authored';

  let best: CoverageRowTier = 'none';
  for (const slot of lineage.sources.sedSlots) {
    const t = slotTier(slot);
    if (t !== null && TIER_RANK[t] > TIER_RANK[best]) best = t;
  }
  // Authored claims may exist on a 'strong' / 'adequate' row that's
  // doc-driven — the slot walk above will have already returned
  // 'doc' / 'provider', which outranks 'authored' per the ladder, so
  // we DON'T promote authored here. Same logic for prior reports.
  // If no slots contributed at all, fall back to the cheapest signal
  // in priority order so the badge still says SOMETHING when an
  // adequate/strong row's sedSlots array is empty (defensive — pure
  // DCT-strong rows shouldn't reach this branch in practice).
  if (best === 'none' && lineage.sources.priorReports.length > 0) best = 'prior-report';
  if (best === 'none' && lineage.sources.authored.length > 0) best = 'authored';
  return best;
}

/**
 * CAF-2 — aggregate per-row attributions into footer-ready counts.
 *
 * Returns one entry per (status, tier) pair so the footer can render:
 *
 *   "2/5 strong (via DAM) + 1/5 strong (via Research) + 1/5 adequate (via provider)"
 *
 * Rows whose tier is `'none'` are excluded from the breakdown (they
 * still show up in the legacy total counts in CoverageMatrix's footer).
 */
export interface TierAttributionEntry {
  status: RequirementStatus;
  tier: Exclude<CoverageRowTier, 'none'>;
  count: number;
}

export function aggregateTierAttribution(
  rows: ReadonlyArray<{ status: RequirementStatus; tier: CoverageRowTier }>,
): TierAttributionEntry[] {
  const buckets = new Map<string, TierAttributionEntry>();
  for (const r of rows) {
    if (r.tier === 'none') continue;
    // Only attribute fills (strong/adequate/authored*). Tentative and
    // missing are already excluded by the per-row helper but we
    // re-check here for safety.
    if (r.status === 'tentative' || r.status === 'missing') continue;
    const key = `${r.status}::${r.tier}`;
    const existing = buckets.get(key);
    if (existing) existing.count += 1;
    else buckets.set(key, { status: r.status, tier: r.tier, count: 1 });
  }
  // Stable ordering: status rank (strong > adequate > authored-strong >
  // authored), then tier rank (authored > doc > prior-report > dam >
  // research > provider). Keeps the footer copy deterministic.
  const statusRank: Record<RequirementStatus, number> = {
    strong: 5,
    'authored-strong': 4,
    adequate: 3,
    authored: 2,
    tentative: 1,
    missing: 0,
  };
  return Array.from(buckets.values()).sort((a, b) => {
    const ds = statusRank[b.status] - statusRank[a.status];
    if (ds !== 0) return ds;
    return TIER_RANK[b.tier] - TIER_RANK[a.tier];
  });
}

/* ── UI sub-components ─────────────────────────────────────────────── */

const TIER_LABEL: Record<Exclude<CoverageRowTier, 'none'>, string> = {
  authored: 'Authored',
  doc: 'Doc',
  'prior-report': 'Prior report',
  dam: 'DAM',
  research: 'AI Research',
  provider: 'Provider',
  liveLit: 'Live lit',
};

/** Single-letter glyph mirroring the EVL-3 LineageChipStrip lettering. */
const TIER_GLYPH: Record<Exclude<CoverageRowTier, 'none'>, string> = {
  authored: 'A',
  doc: 'F',
  'prior-report': 'H',
  dam: 'D',
  research: 'R',
  provider: 'P',
  // LX-3 — `L` for Live literature, distinct from the existing tier
  // glyphs. Paired with the cyan palette below to keep the chip
  // visually separable from the warmer DAM/Research badges.
  liveLit: 'L',
};

/**
 * LX-3 — per-tier class strings. Most tiers reuse the legacy
 * neutral `border-border-subtle text-text-secondary` palette so the
 * existing chip layout doesn't shift. `liveLit` uses the semantic `info`
 * token (theme-aware cyan) so the badge stays readable on light and dark
 * matrix surfaces — raw `cyan-300` on a light tint washed out.
 */
const TIER_CLASS: Record<Exclude<CoverageRowTier, 'none'>, string> = {
  authored: 'border-border-subtle text-text-secondary',
  doc: 'border-border-subtle text-text-secondary',
  'prior-report': 'border-border-subtle text-text-secondary',
  dam: 'border-border-subtle text-text-secondary',
  research: 'border-border-subtle text-text-secondary',
  provider: 'border-border-subtle text-text-secondary',
  liveLit: 'border-info/40 bg-info/10 text-info',
};

/**
 * CAF-3 — per-row tier badge. Renders nothing for `'none'` so missing
 * / tentative / unattributed rows stay visually quiet. Sourcing is
 * the slot's status (DCO-stamped); no chip is rendered unless the
 * status union explicitly carries the tier (hallucination guard).
 */
export const CoverageTierBadge: React.FC<{
  lineage: RequirementLineage;
  /** Optional id override for testids (defaults to lineage.requirementId). */
  requirementId?: string;
}> = ({ lineage, requirementId }) => {
  const tier = attributeRowTier(lineage);
  if (tier === 'none') return null;
  const reqId = requirementId ?? lineage.requirementId;
  const label = TIER_LABEL[tier];
  const glyph = TIER_GLYPH[tier];
  // LX-3 — the `liveLit` channel is informational ("here are papers
  // the LLM may cite") rather than a structured slot fill, so the
  // verb reads "Available via" instead of the CAF-3 "Auto-filled via".
  // Every other tier keeps the original CAF-3 wording.
  const verb = tier === 'liveLit' ? 'Available via' : 'Auto-filled via';
  // Surface the source URL / title when DAM or Research stamped the
  // row — pulled from the first slot whose status matches the tier.
  // The tooltip is the brief's "Hallucination guard: every CAF render
  // must surface the provenance chip + source" contract.
  const source = findTierSource(lineage, tier);
  const title = source
    ? `${verb} ${label} — ${source}`
    : `${verb} ${label}`;
  // LX-3 — the `liveLit` tier reads cyan (informational) instead of
  // the warmer DAM/Research palette so the user can distinguish a
  // literature reference from a structured slot fill. All other tiers
  // keep their legacy neutral palette unchanged.
  const tierClass = TIER_CLASS[tier];
  return (
    <span
      data-testid={`coverage-tier-badge-${reqId}`}
      data-tier={tier}
      title={title}
      className={`inline-flex items-center gap-0.5 px-1 border type-body uppercase tracking-widest font-mono ${tierClass}`}
      aria-label={`${verb} ${label}`}
    >
      <span aria-hidden="true">{glyph}</span>
      <span>{label}</span>
    </span>
  );
};

/**
 * Resolve a human-readable source string for the tier badge tooltip.
 * Walks the lineage's slot rows; never invents a source. Returns
 * `null` when the tier isn't backed by an in-projection slot (e.g.
 * authored or prior-report tiers that don't live on sedSlots).
 */
function findTierSource(
  lineage: RequirementLineage,
  tier: Exclude<CoverageRowTier, 'none'>,
): string | null {
  // Authored tier — surface the first authored claim's confidence band.
  if (tier === 'authored') {
    const top = lineage.sources.authored[0];
    if (!top) return null;
    return `authored claim "${truncate(top.claim ?? '', 60)}"`;
  }
  // Prior-report tier — surface the source report id.
  if (tier === 'prior-report') {
    const top = lineage.sources.priorReports[0];
    if (!top) return null;
    return `${top.reportType} report ${top.reportId}`;
  }
  // LX-3 — Live-literature tier: surface the entry count plus the
  // top-relevance paper's title + provider. Hallucination guard: we
  // ONLY emit the source string when the lineage carries the channel
  // (the badge wouldn't render otherwise; this is defensive).
  if (tier === 'liveLit') {
    const ll = lineage.sources.liveLiterature;
    if (!ll) return null;
    if (!ll.topEntry) return `${ll.entryCount} paper${ll.entryCount === 1 ? '' : 's'}`;
    const t = ll.topEntry;
    return `${ll.entryCount} paper${ll.entryCount === 1 ? '' : 's'} — top: "${truncate(t.title, 50)}" (${t.provider} ${t.year})`;
  }
  // DAM / Research / Doc / Provider — walk sedSlots for the first slot
  // whose status maps to the tier.
  for (const slot of lineage.sources.sedSlots) {
    if (slotTier(slot) !== tier) continue;
    if (tier === 'dam' || tier === 'research') {
      // DCO writes the source URL+title onto `provenanceDetail.researchSource`
      // for research; DAM writes the entry label onto `slot.source`.
      // The lineage projection collapses these into `slot.providerLabel`
      // + `slot.providerKey`, so we read both.
      const label = slot.providerLabel ?? slot.providerKey ?? '';
      return label ? `${label} (${slot.sectionId})` : slot.sectionId;
    }
    if (tier === 'doc') {
      const name = slot.docNames[0];
      return name ? `${name} (${slot.sectionId})` : slot.sectionId;
    }
    if (tier === 'provider') {
      const label = slot.providerLabel ?? slot.providerKey ?? 'provider';
      return `${label} (${slot.sectionId})`;
    }
  }
  return null;
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

/**
 * CAF-3 — Coverage Matrix tier-attributed summary footer.
 *
 * Renders a single line under the legacy `summary-counts` row:
 *
 *   "Auto-filled: 2/5 strong (via DAM) · 1/5 strong (via Research) · 1/5 adequate (via Provider)"
 *
 * When no rows have a tier attribution (e.g. a totally empty SED or
 * one where all fills were from docs the user uploaded), the
 * component renders nothing. This matches the brief's "Never render
 * an unsourced fill" guard.
 */
export const CoverageTierFooter: React.FC<{
  rows: ReadonlyArray<{ status: RequirementStatus; tier: CoverageRowTier }>;
  /** Denominator for the X/Y framing — typically `rows.length`. */
  totalRows: number;
  /** Optional testid suffix (defaults to root). */
  testId?: string;
}> = ({ rows, totalRows, testId = 'coverage-tier-footer' }) => {
  const entries = React.useMemo(() => aggregateTierAttribution(rows), [rows]);
  if (entries.length === 0) return null;
  return (
    <div
      className="type-body text-text-muted flex items-center gap-2 flex-wrap"
      data-testid={testId}
    >
      <span className="font-mono uppercase tracking-widest">Auto-filled:</span>
      {entries.map((e, i) => (
        <React.Fragment key={`${e.status}-${e.tier}`}>
          {i > 0 && <span aria-hidden="true">·</span>}
          <span
            data-testid={`${testId}-entry-${e.status}-${e.tier}`}
            data-status={e.status}
            data-tier={e.tier}
          >
            {e.count}/{totalRows} {statusLabel(e.status)} (via {TIER_LABEL[e.tier]})
          </span>
        </React.Fragment>
      ))}
    </div>
  );
};

function statusLabel(s: RequirementStatus): string {
  switch (s) {
    case 'strong': return 'strong';
    case 'authored-strong': return 'strong';
    case 'adequate': return 'adequate';
    case 'authored': return 'authored';
    case 'tentative': return 'tentative';
    case 'missing': return 'missing';
  }
}
